// (5) Orchestrator for the attempt ladder:
// IDLE → ANSWERING → GRADING → RESULT → [ATTEMPT_2 | ATTEMPT_3 | DONE]
// Wrong once → hint; twice → video and theory; three times → Pink, and the
// question opens its scaffold.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { answerReviewMessage, wordLimitFor, youTubeEmbedUrl } from '@cwy/config/contracts';
import type { ExamTheoryDTO } from '@cwy/config/contracts';
import { apiGet, apiPost } from '@/lib/api';
import type { Attempt, BinColor, Question, SupportPayload } from '@/types';
import { nextAttemptIndex, useExamStore } from '@/store/examStore';
import { useGradingPoller } from '@/hooks/useGradingPoller';
import { QuestionDisplay } from '@/components/exam/QuestionDisplay';
import { AttemptBar } from '@/components/exam/AttemptBar';
import { MCQInput } from '@/components/exam/MCQInput';
import { TypedInput } from '@/components/exam/TypedInput';
import { ScanUpload } from '@/components/exam/ScanUpload';
import { GradingSpinner } from '@/components/exam/GradingSpinner';
import { BinReveal } from '@/components/exam/BinReveal';
import { ScaffoldReveal } from '@/components/exam/ScaffoldReveal';
import { HelplineDrawer } from '@/components/exam/HelplineDrawer';
import { TheoryNoteView } from '@/components/theory/TheoryNoteView';
import { MathText } from '@/components/exam/MathText';
import { MotivationBanner } from '@/components/MotivationBanner';
import { WatchVideosButton } from '@/components/video/VideoLibrary';

type AttemptResponse =
  | {
      finalized: true;
      isCorrect: boolean;
      finalBin: BinColor;
      earnedMarks: number;
      attemptsUsed: number;
      overTime: boolean;
      scaffoldActive: boolean;
    }
  | {
      finalized: false;
      isCorrect: false;
      nextState: string;
      nextAttemptIndex: number;
      support?: SupportPayload | null;
    }
  | { pending: true; jobId: string };

const NO_ATTEMPTS: Attempt[] = [];

/**
 * The answer panel. An exam runs exactly two clocks: the paper's, in the
 * header, and this question's — the one that separates Green from Purple,
 * which starts when the ladder mounts.
 */
export function AnswerPanel({ onClose }: { onClose: () => void }) {
  const activeId = useExamStore((s) => s.activeQuestionId);
  // Keyed so each question starts with its own clock.
  return <AnswerLadder key={activeId ?? ''} onClose={onClose} />;
}

/** Increment an effort counter by 1s while `active` is true. */
function useEffortTimer(type: 'hint' | 'video' | 'theory', active: boolean) {
  const increment = useExamStore((s) => s.incrementEffort);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => increment(type, 1), 1000);
    return () => window.clearInterval(id);
  }, [type, active, increment]);
}

function AnswerLadder({ onClose }: { onClose: () => void }) {
  const spId = useExamStore((s) => s.spId);
  const questions = useExamStore((s) => s.questions);
  const activeId = useExamStore((s) => s.activeQuestionId);
  const result = useExamStore((s) => (activeId ? s.results[activeId] : undefined));
  const recordAttempt = useExamStore((s) => s.recordAttempt);
  const recordResult = useExamStore((s) => s.recordResult);
  const pendingJobId = useExamStore((s) => s.pendingJobId);
  const setPendingJob = useExamStore((s) => s.setPendingJob);
  const openLadderGrade = useExamStore((s) => s.openLadderGrade);
  const setOpenLadderGrade = useExamStore((s) => s.setOpenLadderGrade);
  const attemptsByQuestion = useExamStore((s) => s.attempts);
  const questionAttempts = activeId ? (attemptsByQuestion[activeId] ?? NO_ATTEMPTS) : NO_ATTEMPTS;
  const reviewAttempt = questionAttempts.find(
    (attempt) => attempt.reviewReason && attempt.isCorrect === null,
  );

  const question = useMemo<Question | undefined>(
    () => questions.find((q) => q.id === activeId),
    [questions, activeId],
  );

  const attemptIndex = useExamStore((s) => (activeId ? nextAttemptIndex(s, activeId) : null));

  const [submitting, setSubmitting] = useState(false);
  const [scanMode, setScanMode] = useState<'typed' | 'scan'>('typed');
  const [theoryOpen, setTheoryOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  // The video library is open — watching there is video effort too.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [typedDraft, setTypedDraft] = useState('');
  // Support unlocked for THIS question, captured from the wrong-attempt response.
  const [support, setSupport] = useState<SupportPayload | null>(null);
  // Transient note for AI grades that need manual review / failed.
  const [gradeNote, setGradeNote] = useState<string | null>(null);
  const startRef = useRef<number>(Date.now());
  const pendingAttemptRef = useRef<{
    answer: string | null;
    method: 'OPTION' | 'TYPED' | 'SCAN';
  } | null>(null);

  // Reset per-question UI state when the active question changes.
  useEffect(() => {
    startRef.current = Date.now();
    setScanMode('typed');
    setShowVideo(false);
    setLibraryOpen(false);
    setSupport(null);
    setGradeNote(null);
    setTypedDraft('');
  }, [activeId]);

  // Poll the AI grading job as a fallback to the socket result.
  const poller = useGradingPoller(pendingJobId);

  // When the real result arrives (socket or poll-complete), clear pending.
  useEffect(() => {
    if ((result || reviewAttempt) && pendingJobId) setPendingJob(null);
  }, [result, reviewAttempt, pendingJobId, setPendingJob]);

  // Apply an async AI grade once its job finishes. The worker runs in a separate
  // process (no socket to this client), so the poller is the delivery channel:
  // we apply its result exactly like a synchronous attempt response.
  useEffect(() => {
    if (!pendingJobId || !activeId || !spId) return;
    const state = poller.data?.state;

    if (state === 'failed') {
      setGradeNote("We couldn't grade that answer. Please try submitting again.");
      setPendingJob(null);
      return;
    }
    if (state !== 'completed' || !poller.data?.result) return;

    const r = poller.data.result;
    const method =
      pendingAttemptRef.current?.method ??
      (question?.type === 'MCQ' ? 'OPTION' : scanMode === 'scan' ? 'SCAN' : 'TYPED');
    const submittedAnswer = pendingAttemptRef.current?.answer ?? null;

    if (r.finalized) {
      recordAttempt({
        id: `${activeId}-${r.attemptIndex}`,
        studentPaperId: spId,
        questionId: activeId,
        attemptIndex: r.attemptIndex,
        submittedAnswer,
        answerMethod: method,
        isCorrect: r.isCorrect,
        secondsTaken: 0,
      });
      recordResult({
        studentPaperId: spId,
        questionId: activeId,
        finalBin: r.finalBin,
        earnedMarks: r.earnedMarks,
        attemptsUsed: r.attemptsUsed,
        overTime: r.overTime,
        scaffoldActive: r.scaffoldActive,
      });
    } else if (r.manualReview) {
      recordAttempt({
        id: `${activeId}-${r.attemptIndex}`,
        studentPaperId: spId,
        questionId: activeId,
        attemptIndex: r.attemptIndex,
        submittedAnswer,
        answerMethod: method,
        isCorrect: null,
        secondsTaken: 0,
        reviewReason: r.reviewReason,
        scanConfidence: r.scanConfidence,
      });
    } else {
      // Wrong but ladder open — record the miss and reveal the unlocked support.
      recordAttempt({
        id: `${activeId}-${r.attemptIndex}`,
        studentPaperId: spId,
        questionId: activeId,
        attemptIndex: r.attemptIndex,
        submittedAnswer,
        answerMethod: method,
        isCorrect: false,
        secondsTaken: 0,
      });
      setSupport(r.support ?? null);
      startRef.current = Date.now();
    }
    pendingAttemptRef.current = null;
    setPendingJob(null);
  }, [
    poller.data,
    pendingJobId,
    activeId,
    spId,
    question,
    scanMode,
    recordAttempt,
    recordResult,
    setPendingJob,
  ]);

  // Same wrong-but-open outcome, arriving over SSE instead. This is the fast path
  // — it lands as soon as the worker publishes, rather than on the next poll tick
  // — so it clears `pendingJobId` and the poller stops on its own.
  useEffect(() => {
    if (!openLadderGrade || !activeId || !spId) return;
    if (openLadderGrade.questionId !== activeId) {
      // Graded while the student was on another question; the ladder state will be
      // re-read when they navigate back. Drop it rather than applying it here.
      setOpenLadderGrade(null);
      return;
    }

    recordAttempt({
      id: `${activeId}-${openLadderGrade.attemptIndex}`,
      studentPaperId: spId,
      questionId: activeId,
      attemptIndex: openLadderGrade.attemptIndex,
      submittedAnswer: pendingAttemptRef.current?.answer ?? null,
      answerMethod: pendingAttemptRef.current?.method ?? 'TYPED',
      isCorrect: false,
      secondsTaken: 0,
    });
    setSupport(openLadderGrade.support);
    startRef.current = Date.now();
    pendingAttemptRef.current = null;
    setOpenLadderGrade(null);
    setPendingJob(null);
  }, [openLadderGrade, activeId, spId, recordAttempt, setOpenLadderGrade, setPendingJob]);

  // Polling gave up without a terminal state. The attempt is persisted server-side,
  // so this is a delivery failure, not a lost answer — say that, and let them move on.
  useEffect(() => {
    if (!poller.timedOut || !pendingJobId) return;
    setGradeNote(
      'This is taking longer than usual. Your answer is saved and will be graded — ' +
        'you can carry on with the other questions.',
    );
    setPendingJob(null);
  }, [poller.timedOut, pendingJobId, setPendingJob]);

  // Reload fallback: if the page was refreshed mid-ladder we no longer hold the
  // inline `support` from the wrong-attempt response, so re-fetch what the
  // server says is currently unlocked. Skipped entirely once `support` is set.
  const supportQuery = useQuery({
    queryKey: ['support', spId, activeId, attemptIndex],
    enabled: Boolean(spId && activeId) && (attemptIndex ?? 1) >= 2 && !result && !support,
    queryFn: () =>
      apiGet<{ support: SupportPayload | null }>(`/student-papers/${spId}/support/${activeId}`),
  });

  // The third-attempt video, searched for the mistake the second answer
  // showed. Asked for separately so the attempt response never waits on a
  // model; until it arrives (or if it never does) the sub-topic search stands.
  const videoHint = useQuery({
    queryKey: ['video-hint', spId, activeId],
    enabled:
      Boolean(spId && activeId) && (attemptIndex ?? 1) >= 3 && !result && question?.type === 'MCQ',
    staleTime: Infinity,
    retry: false,
    queryFn: () =>
      apiGet<{ video: { url: string; query: string; focus: string } | null }>(
        `/student-papers/${spId}/video-hint/${activeId}`,
      ),
  });

  useEffortTimer('hint', !result && (attemptIndex ?? 1) >= 2);
  useEffortTimer('video', (showVideo || libraryOpen) && !result);
  useEffortTimer('theory', theoryOpen);

  if (!question || !activeId || !spId) return null;

  const secondsTaken = () => Math.max(1, Math.round((Date.now() - startRef.current) / 1000));
  const idx = attemptIndex ?? 1;

  async function submit(payload: {
    answer: string | null;
    answerMethod: 'OPTION' | 'TYPED' | 'SCAN' | 'SKIPPED';
    s3Key?: string;
  }) {
    if (!activeId || !spId) return;
    setSubmitting(true);
    const thisIndex = idx;
    try {
      const res = await apiPost<AttemptResponse>(`/student-papers/${spId}/attempts`, {
        questionId: activeId,
        attemptIndex: thisIndex,
        answer: payload.answer,
        answerMethod: payload.answerMethod,
        secondsTaken: secondsTaken(),
        s3Key: payload.s3Key,
      });

      if ('pending' in res) {
        pendingAttemptRef.current = {
          answer: payload.answer,
          method: payload.answerMethod as 'OPTION' | 'TYPED' | 'SCAN',
        };
        setPendingJob(res.jobId);
        return;
      }

      const localAttempt: Attempt = {
        id: `${activeId}-${thisIndex}`,
        studentPaperId: spId,
        questionId: activeId,
        attemptIndex: thisIndex,
        submittedAnswer: payload.answer,
        answerMethod: payload.answerMethod,
        isCorrect: res.isCorrect,
        secondsTaken: secondsTaken(),
      };
      recordAttempt(localAttempt);

      if (res.finalized) {
        recordResult({
          studentPaperId: spId,
          questionId: activeId,
          finalBin: res.finalBin,
          earnedMarks: res.earnedMarks,
          attemptsUsed: res.attemptsUsed,
          overTime: res.overTime,
          scaffoldActive: res.scaffoldActive,
        });
      } else {
        // Wrong but ladder open — reveal the unlocked support tier and reset
        // the timer for the next attempt.
        setSupport(res.support ?? null);
        startRef.current = Date.now();
      }
    } catch (err) {
      setGradeNote(
        err instanceof Error ? err.message : 'Could not submit your answer. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  const grading = Boolean(pendingJobId) || poller.data?.state === 'active';
  // Inline support (live) takes precedence; the resolver query is the reload fallback.
  const activeSupport: SupportPayload | null = support ?? supportQuery.data?.support ?? null;
  const hint =
    activeSupport?.content.tipsText?.trim() ||
    activeSupport?.content.strongHintText?.trim() ||
    null;
  const videoRef = activeSupport?.content.videoRef ?? null;
  const embed = youTubeEmbedUrl(videoRef);
  const mistakeVideo = videoHint.data?.video ?? null;
  // Only a limit the paper actually set. A bank question with none must not
  // suddenly tell a student "40 words" because that is the editor's default.
  const wordLimit = question.wordLimit ? wordLimitFor(question) : null;
  const previousTypedAnswer = [...questionAttempts]
    .reverse()
    .find(
      (attempt) => attempt.answerMethod === 'TYPED' && attempt.submittedAnswer,
    )?.submittedAnswer;
  const submitAnyway = question.type !== 'MCQ' && idx >= 2;

  return (
    <div className="mx-auto max-w-2xl px-3 py-4">
      <button
        disabled={grading}
        onClick={onClose}
        className="mb-3 text-sm text-slate-500 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
      >
        {grading ? 'Grading this answer…' : '← All questions'}
      </button>

      <div className="rounded-2xl border border-slate-200 bg-card p-4 shadow-sm">
        <QuestionDisplay question={question} />

        <div className="my-4">
          <AttemptBar
            attemptIndex={result ? result.attemptsUsed : idx}
            scaffold={Boolean(result?.scaffoldActive)}
          />
        </div>

        {/* RESULT */}
        {result ? (
          <>
            <BinReveal
              bin={result.finalBin}
              earnedMarks={result.earnedMarks}
              maxMarks={question.maxMarks}
              overTime={result.overTime}
            />
            {/* The server's own verdict that this ladder closed in Pink — the
                same flag its scaffold routes open on. */}
            {result.scaffoldActive ? (
              <ScaffoldReveal spId={spId} questionId={activeId} onContinue={onClose} />
            ) : (
              <button
                onClick={onClose}
                className="mt-2 w-full rounded-xl bg-slate-900 px-4 py-3 font-semibold text-white hover:bg-slate-800"
              >
                Next question
              </button>
            )}
          </>
        ) : reviewAttempt ? (
          <div className="rounded-xl border border-line bg-paper p-4" role="status">
            <h2 className="font-semibold text-forest">Flagged for your teacher</h2>
            <p className="mt-2 text-sm text-ink-soft">
              {answerReviewMessage(reviewAttempt.reviewReason!)}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 rounded-full bg-forest px-4 py-2 text-sm font-semibold text-white"
            >
              Continue to other questions
            </button>
          </div>
        ) : grading ? (
          /* GRADING */
          <GradingSpinner />
        ) : (
          /* ANSWERING (+ attempt-2 / attempt-3 support) */
          <>
            {gradeNote && (
              <div className="mb-4 rounded-xl border border-info/40 bg-info-bg p-3 text-sm text-info">
                {gradeNote}
              </div>
            )}

            {idx >= 2 && (
              <MotivationBanner
                attemptIndex={idx}
                topicName={question.topicName}
                questionId={question.id}
              />
            )}

            {/* One hint, opened by the first wrong try and kept for the rest
                of the question. A question written with two shows its first,
                or its second when that is all it has. */}
            {idx >= 2 && (
              <div className="mb-4 rounded-xl border border-bin-orange-border bg-bin-orange-bg p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-bin-orange-text">
                  Hint
                </p>
                {hint ? (
                  <p className="mt-1 text-sm text-bin-orange-text">
                    <MathText>{hint}</MathText>
                  </p>
                ) : supportQuery.isLoading ? (
                  <p className="text-sm text-bin-orange-text">Loading a hint…</p>
                ) : (
                  <p className="text-sm text-bin-orange-text">
                    Think about the key formula for this topic.
                  </p>
                )}
              </div>
            )}

            {idx >= 3 && (
              <div className="mb-4 space-y-3">
                {embed &&
                  (showVideo ? (
                    <div className="aspect-video w-full overflow-hidden rounded-xl">
                      <iframe
                        src={embed}
                        title="Concept video"
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowVideo(true)}
                      className="w-full rounded-xl border border-slate-200 bg-card px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      ▶ Watch concept video
                    </button>
                  ))}
                {/* Videos on the student's own mistake, once the search for
                    it is back. Alongside a video the teacher chose, never
                    instead of it. */}
                {mistakeVideo && (
                  <div>
                    <WatchVideosButton
                      source={{ query: mistakeVideo.query }}
                      title={mistakeVideo.focus || 'Videos on your mistake'}
                      onOpenChange={setLibraryOpen}
                      className="block w-full rounded-xl border border-slate-200 bg-card px-4 py-3 text-center text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      ▶ Watch videos on your mistake
                    </WatchVideosButton>
                    {mistakeVideo.focus && (
                      <p className="mt-1.5 text-xs text-slate-600">
                        Watch for: {mistakeVideo.focus}
                      </p>
                    )}
                  </div>
                )}
                {/* A ref that names no single video — a search on the
                    sub-topic, which is what publish sets when the teacher
                    chose none — opens the video library, which finds the
                    videos and plays them here. Without it the lifeline
                    silently vanished. */}
                {!embed && !mistakeVideo && videoRef && (
                  <WatchVideosButton
                    source={{ videoRef }}
                    title="Videos on this sub-topic"
                    onOpenChange={setLibraryOpen}
                    className="block w-full rounded-xl border border-slate-200 bg-card px-4 py-3 text-center text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    ▶ Watch videos on this sub-topic
                  </WatchVideosButton>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setTheoryOpen(true)}
                    className="flex-1 rounded-xl border border-slate-200 bg-card px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Read Theory
                  </button>
                </div>
              </div>
            )}

            {/* Input by question type */}
            {question.type === 'MCQ' ? (
              <MCQInput
                options={question.options ?? []}
                submitting={submitting}
                onSubmit={(selectedIdx) =>
                  submit({ answer: String(selectedIdx), answerMethod: 'OPTION' })
                }
              />
            ) : (
              <div>
                <div className="mb-2 flex gap-2 text-sm">
                  <button
                    onClick={() => setScanMode('typed')}
                    className={`rounded-full px-3 py-1 ${scanMode === 'typed' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    Type
                  </button>
                  <button
                    onClick={() => setScanMode('scan')}
                    className={`rounded-full px-3 py-1 ${scanMode === 'scan' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    Scan / Upload
                  </button>
                </div>
                {wordLimit !== null && (
                  <p className="mb-2 text-xs text-slate-500">Answer in about {wordLimit} words.</p>
                )}
                {scanMode === 'typed' ? (
                  <TypedInput
                    key={question.id}
                    submitting={submitting}
                    initialValue={previousTypedAnswer ?? ''}
                    onValueChange={setTypedDraft}
                    onSubmit={(answer) => submit({ answer, answerMethod: 'TYPED' })}
                  />
                ) : (
                  <ScanUpload
                    questionId={question.id}
                    studentPaperId={spId}
                    onUploaded={(s3Key) => submit({ answer: null, answerMethod: 'SCAN', s3Key })}
                  />
                )}
              </div>
            )}

            <button
              type="button"
              disabled={submitting}
              onClick={() =>
                submit({
                  answer:
                    submitAnyway && scanMode === 'typed'
                      ? typedDraft.trim() || previousTypedAnswer || null
                      : null,
                  answerMethod: 'SKIPPED',
                })
              }
              className="mt-3 w-full rounded-xl border border-bin-pink-border bg-bin-pink-bg px-4 py-2.5 text-sm font-semibold text-bin-pink-text transition hover:bg-bin-pink-fill hover:text-bin-pink-deep disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitAnyway ? 'Submit anyway' : 'Mark as unanswered'}
            </button>
            <p className="mt-1 text-center text-xs text-slate-500">
              {submitAnyway
                ? 'Keep your current response as final and close this question in Pink.'
                : 'This closes the question and places it in Pink.'}
            </p>
          </>
        )}
      </div>

      {/* Theory note drawer (unlocked content during attempt 3). */}
      <HelplineDrawer
        open={theoryOpen}
        onClose={() => setTheoryOpen(false)}
        title="Theory note"
        locked={false}
      >
        <TheoryDrawerBody
          spId={spId}
          questionId={activeId}
          open={theoryOpen}
          fallback={activeSupport?.content.theoryRef ?? null}
        />
      </HelplineDrawer>
    </div>
  );
}

interface QuestionTheory {
  title: string;
  explanation: string;
  keyPoints: string[];
}

/**
 * The theory drawer: a note on the idea THIS question tests, then the
 * uploaded note for its sub-topic (or its topic), or the one-line reference
 * when CWY has not written one yet.
 *
 * Both are fetched when the drawer opens rather than with the question: the
 * server releases them only once the ladder has unlocked the theory rung, so
 * asking earlier would only be refused. The question's note is written by a
 * model and can be slow or absent; the stored note never waits on it.
 */
function TheoryDrawerBody({
  spId,
  questionId,
  open,
  fallback,
}: {
  spId: string | null;
  questionId: string | null;
  open: boolean;
  fallback: string | null;
}) {
  const theory = useQuery({
    queryKey: ['exam-theory', spId, questionId],
    enabled: open && Boolean(spId && questionId),
    staleTime: Infinity,
    queryFn: () =>
      apiGet<{ theory: ExamTheoryDTO | null }>(`/student-papers/${spId}/theory/${questionId}`),
  });
  const forQuestion = useQuery({
    queryKey: ['exam-theory-hint', spId, questionId],
    enabled: open && Boolean(spId && questionId),
    staleTime: Infinity,
    retry: false,
    queryFn: () =>
      apiGet<{ theory: QuestionTheory | null }>(
        `/student-papers/${spId}/theory-hint/${questionId}`,
      ),
  });

  const questionNote = forQuestion.data?.theory ?? null;
  const note = theory.data?.theory;
  return (
    <div className="flex flex-col gap-4">
      {forQuestion.isLoading ? (
        <p className="animate-pulse rounded-xl bg-slate-100 p-3 text-xs text-slate-500">
          Writing a note on this question…
        </p>
      ) : (
        questionNote && (
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-fs-10 font-bold uppercase tracking-[0.08em] text-slate-500">
              For this question
            </p>
            <h4 className="mt-1 text-sm font-semibold text-ink">
              <MathText>{questionNote.title}</MathText>
            </h4>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
              <MathText>{questionNote.explanation}</MathText>
            </p>
            {questionNote.keyPoints.length > 0 && (
              <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm text-slate-700">
                {questionNote.keyPoints.map((point, i) => (
                  <li key={i}>
                    <MathText>{point}</MathText>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )
      )}

      {theory.isLoading ? (
        <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
      ) : note ? (
        <TheoryNoteView
          note={note.content}
          scopeNote={note.scope === 'CHAPTER' ? `From the topic ${note.nodeName}.` : undefined}
        />
      ) : (
        <p className="text-sm leading-relaxed text-slate-700">
          {fallback ?? 'Theory note coming soon for this sub-topic.'}
        </p>
      )}
    </div>
  );
}
